/*
 * bundler-volume-core.js
 *
 * Shared, framework-free helpers for reading the Bundler app's volume-discount
 * offers from window.bndlr.getBundles() and computing per-tier prices. Bundler
 * does not expose this data in Liquid, so every surface that needs to show its
 * discounted prices (product cards, the cart) computes them client-side from
 * the same offer payload. This module keeps that math in ONE place so the
 * rounding stays identical everywhere (and matches the PDP / real checkout).
 *
 * Exposed as window.BundlerVolume:
 *   getVolumeOffers(bundles)            -> [offer]   enabled volume offers
 *   offerAppliesToProduct(offer, id)    -> Boolean
 *   extractTiers(offer)                 -> [{minQty, discountType, discountValue}]
 *   bestTierForQty(tiers, qty)          -> tier | null   highest minQty <= qty
 *   computeTier(baseNetCents, tier)     -> {perUnitCents, pct}
 *   normaliseId(value)                  -> String | null
 *
 * NOTE: the exact shape of getBundles() is not documented. extractTiers /
 * offerAppliesToProduct probe several common field names ("SHAPE-DEPENDENT").
 */
(function () {
  'use strict';

  // Normalise an id (number, numeric string, or gid://shopify/Product/123) to
  // its bare numeric string so card / cart data-product-id matches Bundler's ids.
  function normaliseId(value) {
    if (value == null) return null;
    var m = String(value).match(/\d+/g);
    return m ? m[m.length - 1] : null;
  }

  function firstDefined(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      if (obj && obj[keys[i]] != null) return obj[keys[i]];
    }
    return undefined;
  }

  /* ---------- shape of Bundler getBundles() ---------- */

  // getBundles() may return an array, a single offer object, or an object keyed
  // by id. Normalise to a flat array of offer objects.
  function toOfferArray(bundles) {
    if (!bundles) return [];
    if (Array.isArray(bundles)) return bundles;
    if (typeof bundles === 'object') {
      // Single offer (has its own id/volume markers) vs. a map of offers.
      if (bundles.volume_discounts || bundles.is_volume_bundle != null) return [bundles];
      return Object.keys(bundles).map(function (k) { return bundles[k]; });
    }
    return [];
  }

  function isTruthyFlag(v) {
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  // Return only the enabled volume-discount offers.
  function getVolumeOffers(bundles) {
    return toOfferArray(bundles).filter(function (b) {
      if (!b || typeof b !== 'object') return false;
      var enabled = b.status == null || String(b.status).toLowerCase() === 'enabled';
      var isVolume = isTruthyFlag(b.is_volume_bundle) || Array.isArray(b.volume_discounts);
      return enabled && isVolume;
    });
  }

  // Does this offer apply to the given (normalised) product id?
  function offerAppliesToProduct(offer, productId) {
    var targetType = String(offer.product_target_type || '').toLowerCase();
    if (targetType.indexOf('all') !== -1) return true; // e.g. "all_products"

    // products is an object keyed by product id, each value carrying its own id.
    var products = offer.products;
    if (products && typeof products === 'object') {
      if (Object.prototype.hasOwnProperty.call(products, productId)) return true;
      for (var k in products) {
        if (products[k] && normaliseId(products[k].id) === productId) return true;
      }
    }
    // NOTE: collection / tag targeting is not handled here. Add it if Bundler
    // offers start using those types.
    return false;
  }

  // Return tiers as [{ minQty, discountType, discountValue }], sorted by qty.
  // discountType: 'percentage' | 'fixed' (amount off) | 'price' (fixed unit price)
  function extractTiers(offer) {
    var raw = offer.volume_discounts;
    if (!Array.isArray(raw)) return [];

    var tiers = raw.map(function (t) {
      var minQty = parseInt(firstDefined(t, ['min_items', 'quantity', 'min_quantity']), 10);
      var dType = String(t.discount_type || offer.discount_type || 'percentage').toLowerCase();
      var dValue = parseFloat(firstDefined(t, ['discount_value', 'value', 'amount']));

      var normType = 'percentage';
      if (dType.indexOf('price') !== -1) normType = 'price';
      else if (dType.indexOf('amount') !== -1 || dType.indexOf('fixed') !== -1) normType = 'fixed';

      return { minQty: minQty, discountType: normType, discountValue: dValue };
    }).filter(function (t) {
      return !isNaN(t.minQty) && !isNaN(t.discountValue);
    });

    tiers.sort(function (a, b) { return a.minQty - b.minQty; });
    return tiers;
  }

  // The tier that applies at `qty`: highest minQty <= qty (mirrors the PDP's
  // highlightVolumeTier "highest threshold ≤ qty" rule). null when below tier 1.
  function bestTierForQty(tiers, qty) {
    var best = null;
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      if (t.minQty <= qty && (best == null || t.minQty > best.minQty)) best = t;
    }
    return best;
  }

  // Resolve overlapping offers the way Bundler's checkout does (verified
  // empirically on qty 3 vs qty 1000 with a specific_products offer and an
  // all_products offer both matching): the specific offer wins when one of its
  // tiers applies at the quantity; otherwise the broader offer supplies the
  // tier. Returns { offer, tier } or null when no tier applies anywhere.
  function bestOfferTierForQty(offers, productId, qty) {
    var matches = [];
    for (var i = 0; i < offers.length; i++) {
      if (offerAppliesToProduct(offers[i], productId)) matches.push(offers[i]);
    }
    matches.sort(function (a, b) {
      var aAll = String(a.product_target_type || '').toLowerCase().indexOf('all') !== -1 ? 1 : 0;
      var bAll = String(b.product_target_type || '').toLowerCase().indexOf('all') !== -1 ? 1 : 0;
      return aAll - bAll;
    });
    for (var j = 0; j < matches.length; j++) {
      var tier = bestTierForQty(extractTiers(matches[j]), qty);
      if (tier) return { offer: matches[j], tier: tier };
    }
    return null;
  }

  /* ---------- tier math ---------- */

  // Returns { perUnitCents, pct } for a tier given the base net unit price.
  function computeTier(baseNetCents, tier) {
    switch (tier.discountType) {
      case 'price': {
        // discountValue is a fixed unit price; Bundler usually stores it in the
        // shop currency major unit. Treat <1000 as euros, else assume cents.
        var perUnit = tier.discountValue < 1000
          ? Math.round(tier.discountValue * 100)
          : Math.round(tier.discountValue);
        var pct = baseNetCents > 0 ? Math.round((1 - perUnit / baseNetCents) * 100) : null;
        return { perUnitCents: perUnit, pct: pct };
      }
      case 'fixed': {
        // discountValue is an amount off per unit (major unit).
        var off = tier.discountValue < 1000
          ? Math.round(tier.discountValue * 100)
          : Math.round(tier.discountValue);
        var per = Math.max(0, baseNetCents - off);
        var p = baseNetCents > 0 ? Math.round((off / baseNetCents) * 100) : null;
        return { perUnitCents: per, pct: p };
      }
      case 'percentage':
      default: {
        var pctVal = tier.discountValue;
        return {
          perUnitCents: Math.round(baseNetCents * (1 - pctVal / 100)),
          pct: Math.round(pctVal)
        };
      }
    }
  }

  window.BundlerVolume = {
    normaliseId: normaliseId,
    firstDefined: firstDefined,
    toOfferArray: toOfferArray,
    isTruthyFlag: isTruthyFlag,
    getVolumeOffers: getVolumeOffers,
    offerAppliesToProduct: offerAppliesToProduct,
    extractTiers: extractTiers,
    bestTierForQty: bestTierForQty,
    bestOfferTierForQty: bestOfferTierForQty,
    computeTier: computeTier
  };
})();
